import React, { useEffect, useState } from "react";
import {
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  IconButton,
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Grid,
  MenuItem,
  Typography,
  Box,
  CircularProgress,
  RadioGroup,
  Radio,
  FormControlLabel,
  Checkbox,
  Tooltip,
} from "@material-ui/core";
import { Edit, Delete, InfoOutlined } from "@material-ui/icons";
import axios from "axios";
import { URL } from "../../config/config";
import { useSnackbarContext } from "../../commons/Snackbar/Snackbar";
const defaultValue = {
  formId: "",
  Webhook: "",
};

// Dropdowns use a white fill with a grey outline so they are visually
// distinguished from the grey-filled text inputs.
const dropdownBoxStyle = {
  backgroundColor: "#fff",
  border: "1px solid #ccc",
  borderRadius: "8px",
};

export default function Flows({ assistantDetails }) {
  const { showSnackbar } = useSnackbarContext();
  const [open, setOpen] = useState(false);
  const [mapedData, setMapedData] = useState([]);
  const [loading, setLoading] = useState(false);
  const [prefilledData, setPrefilledData] = useState();
  const [headers, setHeaders] = useState([{ key: "", value: "" }]);
  const [formFields, setFormFields] = useState(defaultValue);
  const [additional, setAdditional] = useState([
    { key: "", value: "", type: "String" },
  ]);
  const [outputMode, setOutputMode] = useState("merge");
  const [nestedTargetKeys, setNestedTargetKeys] = useState(false);
  const [fieldMappings, setFieldMappings] = useState([
    { targetKey: "", sourceType: "field", value: "", dataType: "String" },
  ]);

  useEffect(() => {
    if (prefilledData) {
      // Populate form fields
      setFormFields({
        formId: prefilledData.formId || "",
        Webhook: prefilledData.callbackConfig?.callbackURL || "",
      });

      // Populate headers
      const prefillHeaders = prefilledData.callbackConfig?.headers
        ? Object.entries(prefilledData.callbackConfig.headers).map(
            ([key, value]) => ({ key, value })
          )
        : [{ key: "", value: "" }];
      setHeaders(prefillHeaders);

      // Populate additional payload
      const prefillAdditional = prefilledData.callbackConfig
        ?.additionalBodyPayload
        ? Object.entries(
            prefilledData.callbackConfig.additionalBodyPayload
          ).map(([key, value]) => ({
            key,
            value: String(value),
            type:
              typeof value === "boolean"
                ? "Boolean"
                : Number.isInteger(value)
                ? "Integer"
                : "String",
          }))
        : [{ key: "", value: "", type: "String" }];
      setAdditional(prefillAdditional);

      // Populate output mode
      setOutputMode(prefilledData.callbackConfig?.outputMode || "merge");
      setNestedTargetKeys(
        Boolean(prefilledData.callbackConfig?.nestedTargetKeys)
      );

      // Populate field mappings
      const prefillMappings = prefilledData.callbackConfig?.fieldMappings?.length
        ? prefilledData.callbackConfig.fieldMappings.map((m) => ({
            targetKey: m.targetKey || "",
            sourceType: m.sourceType || "field",
            value:
              m.sourceType === "static"
                ? m.staticValue ?? ""
                : m.sourceKey ?? "",
            dataType: m.dataType || "String",
          }))
        : [{ targetKey: "", sourceType: "field", value: "", dataType: "String" }];
      setFieldMappings(prefillMappings);
    }
  }, [prefilledData]);

  const handleAddHeader = () => {
    setHeaders([...headers, { key: "", value: "" }]);
  };

  const handleRemoveHeader = (index) => {
    setHeaders(headers.filter((_, i) => i !== index));
  };

  const handleAddAdditional = () => {
    setAdditional([...additional, { key: "", value: "", type: "String" }]);
  };

  const handleRemoveAdditional = (index) => {
    setAdditional(additional.filter((_, i) => i !== index));
  };

  const handleAddMapping = () => {
    setFieldMappings([
      ...fieldMappings,
      { targetKey: "", sourceType: "field", value: "", dataType: "String" },
    ]);
  };

  const handleRemoveMapping = (index) => {
    setFieldMappings(fieldMappings.filter((_, i) => i !== index));
  };

  const handleMappingChange = (e, index) => {
    const { name, value } = e.target;
    const newMappings = [...fieldMappings];
    newMappings[index][name] = value;
    setFieldMappings(newMappings);
  };

  const handleFormChange = (e) => {
    const { name, value } = e.target;
    setFormFields((prevFields) => ({
      ...prevFields,
      [name]: value,
    }));
  };

  const handleHeaderChange = (e, index) => {
    const { name, value } = e.target;
    const newHeaders = [...headers];
    newHeaders[index][name] = value;
    setHeaders(newHeaders);
  };

  const handleAdditionalChange = (e, index) => {
    const { name, value } = e.target;
    const newAdditional = [...additional];
    newAdditional[index][name] = value;
    setAdditional(newAdditional);
  };

  const fetchForms = async () => {
    try {
      const response = await axios.post(URL + "/superadmin/wa-forms/forms", {
        assistantId: assistantDetails._id,
      });
      setMapedData(response?.data?.forms);
    } catch (error) {
      console.error(error);
    }
  };

  useEffect(() => {
    fetchForms();
  }, []);

  const cancelhandler = () => {
    setOpen(false);
    setPrefilledData([]);
    setHeaders([]);
    setFormFields(defaultValue);
    setAdditional([]);
    setOutputMode("merge");
    setFieldMappings([
      { targetKey: "", sourceType: "field", value: "", dataType: "String" },
    ]);
  };

  async function createFormHandler() {
    setLoading(true);
    const headersObject = headers.reduce((acc, header) => {
      if (header.key && header.value) {
        acc[String(header.key)] = header.value; // Ensure keys are strings
      }
      return acc;
    }, {});

    const additionalPayloadObject = additional.reduce((acc, item) => {
      let formattedValue = item.value;

      // Convert value type based on selected type
      if (item.type === "Boolean") {
        formattedValue = item.value.toLowerCase() === "true";
      } else if (item.type === "Integer") {
        formattedValue = parseInt(item.value, 10) || 0;
      }

      acc[String(item.key)] = formattedValue; // Ensure keys are strings
      return acc;
    }, {});

    const fieldMappingsPayload = fieldMappings
      .filter(
        (m) =>
          m.targetKey &&
          (m.sourceType === "static" || m.value) // static may have empty value
      )
      .map((m) => {
        const entry = {
          targetKey: m.targetKey,
          sourceType: m.sourceType,
          dataType: m.dataType,
        };
        if (m.sourceType === "static") {
          entry.staticValue = m.value;
        } else {
          entry.sourceKey = m.value;
        }
        return entry;
      });

    const payload = {
      assistantId: assistantDetails._id,
      clientId: assistantDetails.clientId,
      callbackConfig: {
        callbackURL: formFields.Webhook,
        headers: headersObject,
        additionalBodyPayload: additionalPayloadObject, // Now correctly formatted
        outputMode,
        nestedTargetKeys,
        fieldMappings: fieldMappingsPayload,
      },
    };

    try {
      const response = await axios.post(
        `${URL}/superadmin/wa-forms/${formFields.formId}/callback`,
        payload,
        {
          headers: {
            "Content-Type": "application/json",
          },
        }
      );

      if (response.data.success) {
        setLoading(false);
        showSnackbar("Webhook has been configured successfully.", "success");
        cancelhandler();
        fetchForms();
      }
    } catch (error) {
      console.error("Error:", error.response?.data?.message || error.message);
      cancelhandler();
      setLoading(false);
      const errMsg=error.response?.data?.message || error.message || 'Something went wrong'
      showSnackbar(errMsg, "error");
    }
  }

  return (
    <>
      <Box>
        <Typography
          style={{
            fontSize: "18px",
            fontWeight: 600,
          }}
        >
          Form Details
        </Typography>
        <Box paddingTop={2}>
          <TableContainer component={Paper}>
            <Table>
              <TableHead
                style={{
                  backgroundColor: "#f0f0f0",
                }}
              >
                <TableRow>
                  <TableCell align="left">
                    <strong>FORM NAME</strong>
                  </TableCell>
                  <TableCell align="center">
                    <strong>FORM ID</strong>
                  </TableCell>
                  <TableCell align="center">
                    <strong>URL</strong>
                  </TableCell>
                  <TableCell align="right">
                    <strong>ACTION</strong>
                  </TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {mapedData && mapedData.length > 0 ? (
                  mapedData.map((row) => (
                    <TableRow key={row.id}>
                      <TableCell align="left">{row.name}</TableCell>
                      <TableCell align="center">{row.formId}</TableCell>
                      <TableCell align="center">
                        {row?.callbackConfig?.callbackURL}
                      </TableCell>
                      <TableCell align="right">
                        <IconButton
                          onClick={() => {
                            setPrefilledData(row);
                            setOpen(true);
                          }}
                          color="primary"
                        >
                          <Edit />
                        </IconButton>
                      </TableCell>
                    </TableRow>
                  ))
                ) : (
                  <TableRow>
                    <TableCell colSpan={4} align="center">
                      No data found.
                    </TableCell>
                  </TableRow>
                )}
              </TableBody>
            </Table>
          </TableContainer>
        </Box>
        <Box
          style={{
            display: "flex",
            alignContent: "left",
            paddingTop: "16px",
            paddingBottom: "16px",
          }}
        >
          <Button
            variant="contained"
            color="primary"
            style={{ marginTop: "10px", float: "right" }}
            onClick={() => setOpen(true)}
          >
            Add WA Form Webhook Configuration
          </Button>
        </Box>

        <Dialog
          open={open}
          onClose={() => {
            cancelhandler();
          }}
        >
          <DialogTitle>
            <Typography
              style={{
                fontSize: "18px",
                fontWeight: 600,
              }}
            >
              Add New Form
            </Typography>
          </DialogTitle>
          <DialogContent
            style={{
              borderBottom: "1px solid #E0E0E0",
              paddingBottom: "20px",
            }}
          >
            <Grid container spacing={2}>
              <Grid item xs={12}>
                <Typography
                  style={{
                    fontSize: "16px",
                    fontWeight: 600,
                  }}
                >
                  WA Form ID{" "}
                  <span
                    style={{
                      color: "red",
                    }}
                  >
                    *
                  </span>
                  <br />
                  <span
                    style={{
                      fontSize: "12px",
                      fontWeight: 400,
                    }}
                  >
                    Please enter your WA form Id as visible in BM
                  </span>
                </Typography>

                <TextField
                  value={formFields.formId}
                  name="formId"
                  placeholder="Form ID"
                  fullWidth
                  onChange={handleFormChange}
                />
              </Grid>
              <Grid item xs={12}>
                <Typography
                  style={{
                    fontSize: "16px",
                    fontWeight: 600,
                  }}
                >
                  Webhook URL{" "}
                  <span
                    style={{
                      color: "red",
                    }}
                  >
                    *
                  </span>
                  <br />
                  <span
                    style={{
                      fontSize: "12px",
                      fontWeight: 400,
                    }}
                  >
                    Please enter the Webhook URL where you will receive the form
                    response.
                  </span>
                </Typography>
                <TextField
                  value={formFields.Webhook}
                  name="Webhook"
                  placeholder="Webhook URL"
                  fullWidth
                  onChange={handleFormChange}
                />
              </Grid>

              <Grid item xs={12}>
                <Typography
                  style={{
                    fontSize: "16px",
                    fontWeight: 600,
                  }}
                >
                  Headers
                  <br />
                  <span
                    style={{
                      fontSize: "12px",
                      fontWeight: 400,
                    }}
                  >
                    These header will be sent along with your Webhook URL.
                  </span>
                </Typography>
              </Grid>
              {headers.map((header, index) => (
                <Grid container item xs={12} spacing={2} key={index}>
                  <Grid item xs={5}>
                    <TextField
                      name="key"
                      placeholder="Key"
                      fullWidth
                      value={header.key}
                      onChange={(e) => handleHeaderChange(e, index)}
                    />
                  </Grid>
                  <Grid item xs={5}>
                    <TextField
                      name="value"
                      placeholder="Value"
                      fullWidth
                      value={header.value}
                      onChange={(e) => handleHeaderChange(e, index)}
                    />
                  </Grid>
                  <Grid item xs={2}>
                    {index !== 0 && (
                      <IconButton onClick={() => handleRemoveHeader(index)}>
                        <Delete />
                      </IconButton>
                    )}
                  </Grid>
                </Grid>
              ))}

              <Button
                variant="outlined"
                color="primary"
                onClick={handleAddHeader}
                style={{
                  marginLeft: "10px",
                }}
              >
                Add More
              </Button>

              <Grid
                style={{
                  marginTop: "10px",
                }}
                item
                xs={12}
              >
                <Typography
                  style={{
                    fontSize: "16px",
                    fontWeight: 600,
                  }}
                >
                  Body
                  <br />
                  <span
                    style={{
                      fontSize: "12px",
                      fontWeight: 400,
                    }}
                  >
                    Additional body data (apart from your form response data)
                    that will be sent along with your Webhook URL.
                  </span>
                </Typography>
              </Grid>

              {additional.map((item, index) => (
                <Grid container item xs={12} spacing={2} key={index}>
                  <Grid item xs={4}>
                    <TextField
                      name="key"
                      placeholder="Key"
                      fullWidth
                      value={item.key}
                      onChange={(e) => handleAdditionalChange(e, index)}
                    />
                  </Grid>
                  <Grid item xs={4}>
                    <TextField
                      name="value"
                      placeholder="Value"
                      fullWidth
                      value={item.value}
                      onChange={(e) => handleAdditionalChange(e, index)}
                    />
                  </Grid>
                  <Grid item xs={3}>
                    <TextField
                      select
                      fullWidth
                      style={dropdownBoxStyle}
                      value={item.type}
                      name="type"
                      onChange={(e) => handleAdditionalChange(e, index)}
                    >
                      <MenuItem value="String">String</MenuItem>
                      <MenuItem value="Integer">Integer</MenuItem>
                      <MenuItem value="Boolean">Boolean</MenuItem>
                    </TextField>
                  </Grid>
                  <Grid item xs={1}>
                    {index !== 0 && (
                      <IconButton onClick={() => handleRemoveAdditional(index)}>
                        <Delete />
                      </IconButton>
                    )}
                  </Grid>
                </Grid>
              ))}

              <Button
                style={{
                  marginLeft: "10px",
                }}
                variant="outlined"
                color="primary"
                onClick={handleAddAdditional}
              >
                Add More
              </Button>

              <Grid
                style={{
                  marginTop: "10px",
                }}
                item
                xs={12}
              >
                <Box
                  display="flex"
                  alignItems="flex-start"
                  justifyContent="space-between"
                  flexWrap="wrap"
                >
                  <Typography
                    component="div"
                    style={{
                      fontSize: "16px",
                      fontWeight: 600,
                    }}
                  >
                    <span
                      style={{
                        display: "inline-flex",
                        alignItems: "center",
                        gap: 4,
                        verticalAlign: "middle",
                      }}
                    >
                      Response Mapping
                      <Tooltip
                        arrow
                        title={
                          'For "Form Field" use the incoming field name (e.g. pincode); for "Variable" use Name, MobileNumber, FirstName or LastName.'
                        }
                      >
                        <InfoOutlined
                          style={{ fontSize: "16px", color: "#888" }}
                        />
                      </Tooltip>
                    </span>
                    <br />
                    <span
                      style={{
                        fontSize: "12px",
                        fontWeight: 400,
                      }}
                    >
                      Rename and format form fields into your destination API
                      schema.
                    </span>
                  </Typography>

                  <Box display="flex" alignItems="center" style={{ gap: 4 }}>
                    <Typography style={{ fontSize: "13px", color: "#666" }}>
                      Output:
                    </Typography>
                    <RadioGroup
                      row
                      value={outputMode}
                      onChange={(e) => setOutputMode(e.target.value)}
                      style={{ flexWrap: "nowrap" }}
                    >
                      <FormControlLabel
                        value="merge"
                        control={<Radio size="small" color="primary" />}
                        label={
                          <span style={{ fontSize: "13px" }}>Merge</span>
                        }
                        style={{ marginRight: "8px" }}
                      />
                      <FormControlLabel
                        value="strict"
                        control={<Radio size="small" color="primary" />}
                        label={
                          <span style={{ fontSize: "13px" }}>Strict</span>
                        }
                        style={{ marginRight: "0px" }}
                      />
                    </RadioGroup>
                    <Tooltip
                      arrow
                      title="Merge: send mapped fields plus any unmapped form fields as-is. Strict: send only the mapped/static/variable fields."
                    >
                      <InfoOutlined
                        style={{ fontSize: "16px", color: "#888" }}
                      />
                    </Tooltip>
                    <FormControlLabel
                      control={
                        <Checkbox
                          size="small"
                          color="primary"
                          checked={nestedTargetKeys}
                          onChange={(e) =>
                            setNestedTargetKeys(e.target.checked)
                          }
                        />
                      }
                      label={
                        <span style={{ fontSize: "13px" }}>
                          Nested target keys
                        </span>
                      }
                      style={{ marginLeft: "8px", marginRight: "0px" }}
                    />
                    <Tooltip
                      arrow
                      title='When on, a Target Field like "compositeRequest[0].body.LastName" builds nested objects and arrays instead of a single flat key. Use for destinations that need an envelope (e.g. Salesforce composite). Leave off for flat payloads.'
                    >
                      <InfoOutlined
                        style={{ fontSize: "16px", color: "#888" }}
                      />
                    </Tooltip>
                  </Box>
                </Box>
              </Grid>

              {fieldMappings.map((item, index) => (
                <Grid
                  container
                  item
                  xs={12}
                  spacing={2}
                  key={index}
                  alignItems="center"
                >
                  <Grid item xs={3}>
                    <TextField
                      name="targetKey"
                      placeholder="Target Field"
                      fullWidth
                      value={item.targetKey}
                      onChange={(e) => handleMappingChange(e, index)}
                    />
                  </Grid>
                  <Grid item xs={3}>
                    <TextField
                      select
                      fullWidth
                      style={dropdownBoxStyle}
                      value={item.sourceType}
                      name="sourceType"
                      onChange={(e) => handleMappingChange(e, index)}
                    >
                      <MenuItem value="field">Form Field</MenuItem>
                      <MenuItem value="static">Static</MenuItem>
                      <MenuItem value="variable">Variable</MenuItem>
                    </TextField>
                  </Grid>
                  <Grid item xs={3}>
                    <TextField
                      name="value"
                      placeholder={
                        item.sourceType === "static"
                          ? "Static value"
                          : item.sourceType === "variable"
                          ? "Variable name"
                          : "Form field name"
                      }
                      fullWidth
                      value={item.value}
                      onChange={(e) => handleMappingChange(e, index)}
                    />
                  </Grid>
                  <Grid item xs={2}>
                    <TextField
                      select
                      fullWidth
                      style={dropdownBoxStyle}
                      value={item.dataType}
                      name="dataType"
                      onChange={(e) => handleMappingChange(e, index)}
                    >
                      <MenuItem value="String">String</MenuItem>
                      <MenuItem value="Integer">Integer</MenuItem>
                      <MenuItem value="Double">Double</MenuItem>
                      <MenuItem value="Boolean">Boolean</MenuItem>
                      <MenuItem value="DateTime">DateTime</MenuItem>
                    </TextField>
                  </Grid>
                  <Grid item xs={1}>
                    {index !== 0 && (
                      <IconButton onClick={() => handleRemoveMapping(index)}>
                        <Delete />
                      </IconButton>
                    )}
                  </Grid>
                </Grid>
              ))}

              <Button
                style={{
                  marginLeft: "10px",
                }}
                variant="outlined"
                color="primary"
                onClick={handleAddMapping}
              >
                Add More
              </Button>
            </Grid>
          </DialogContent>

          <DialogActions
            style={{
              boxSizing: "border-box",
            }}
          >
            <Box
              style={{
                display: "flex",
              }}
            >
              <Button
                variant="outlined"
                color="primary"
                style={{
                  marginTop: "10px",
                  float: "right",
                  marginRight: "10px",
                }}
                onClick={() => {
                  cancelhandler();
                }}
              >
                Cancel
              </Button>
              <Button
                variant="contained"
                color="primary"
                style={{ marginTop: "10px", float: "right" }}
                onClick={createFormHandler}
                disabled={
                  loading ||
                  formFields.Webhook === "" ||
                  formFields.formId === ""
                }
                endIcon={
                  loading ? (
                    <CircularProgress size={20} color="inherit" />
                  ) : null
                }
              >
                Submit
              </Button>
            </Box>
          </DialogActions>
        </Dialog>
      </Box>
    </>
  );
}
